import React, { ChangeEvent, useState } from 'react'
import { SwatchesPicker  } from 'react-color'
import { ICreateProfile, IUpdateProfile } from '../../@types';
import { CloseIcon, PDFIcon } from '../../assets/icons';
import UploadIcon from '../../assets/icons/UploadIcon';
import { shortenFileName } from '../../utils';


interface BrandIdentityProps {
  setActiveStep: (step: number) => void;
  handleUpdateProfile: (profile: IUpdateProfile ) => Promise<void>;
}

const MAX_FILE_SIZE_KB= 100;

const BrandIdentity: React.FC<BrandIdentityProps> =({ setActiveStep,  handleUpdateProfile }) => {

  const toBase64 = (file: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const [selectedColor, setSelectedColor] = useState<string | undefined>(undefined);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>();
  const [error, setError] = React.useState<string>();
  const [fileName, setFileName] = React.useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectFile = () => {
    inputRef.current?.click();
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (selectedFile) {
      const fileSizeInKB = selectedFile.size / 1024;
      if (fileSizeInKB > MAX_FILE_SIZE_KB) {
        setError(`File size should not exceed ${MAX_FILE_SIZE_KB} KB`); 
        setFile(null);
        setFileName('');
        return;
      }

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please upload a PNG, JPG, or JPEG file');
        setFile(null);
        setFileName('');
        return;
      }

      setFile(selectedFile);
      setFileName(selectedFile.name); 
      setError(undefined);
    }
  };

  const handlePrevious = () => {
    //@ts-ignore
    setActiveStep((prev: number) => prev - 1);
  };
  
  
  const handleNext = async () => {
    if (file) {
      try {
        setIsLoading(true);
        const base64Logo = await toBase64(file);
        const profileData: IUpdateProfile = {
          //@ts-ignore
          brandLogo: base64Logo,
          brandColor: selectedColor ?? '',
          firstName: '',
          lastName: '',
          businessName: '',
        };

        await handleUpdateProfile(profileData);
        //@ts-ignore
        setActiveStep((prev) => prev + 1);
      } catch (error) {
        console.error("Error submitting the profile data:", error);
        setError('Error submitting the profile data');
      } finally {
        setIsLoading(false);
      }
    }
  };

  
  
  
  const handleSkip = () => {
    handleNext();
  };

  const handleColorChange = (color: any) => {
    setSelectedColor(color.hex);
  };

  const renderErrorMessage = () => {
    if (error) {
      return (
        <div className="text-red-500 mt-2">
          {error}
        </div>
      );
    }
    return null;
  };

  return (
    <>
    <p className='top-0 right-0 text-[#044E97] flex justify-end text-[16px] mt-[3em] xl:mr-[5em] mr-[2em] biglg:mr-[5em] font-medium' onClick={handleSkip}  style={{ cursor: 'pointer' }}>Skip</p>
    <div className='md:mt-[12em] lg:ml-[3em] biglg:ml-[7em] ml-0 xl:ml-[10em] mt-[5em]'>
        <h2 className="lg:text-[24px] text-[20px] md:text-[24px] bigmd:text-[24px] text-[#292D32] font-bold  xl:ml-[0.6em]">Brand Identity</h2>
      <p className="mb-4 lg:text-[16px] font-light text-[14px] md:text-[16px] bigmd:text-[16px] xl:ml-[1em]">Upload your logo and select a color that suits your brand</p>
      <div className='xl:text-[16px] font-light  xl:ml-[1em] mt-[2em]'>
        <p>Upload Brand Logo</p>
        <div className="flex  mt-[13px]">
          <button
            onClick={handleSelectFile}
            className="flex items-center border border-[#828282] px-[13px] py-[8px] rounded-[4px] bg-[#EDF2F5] "
          >
            <p>Upload document</p>
            <div className="bg-[#D6DADD] w-[1px] h-full mx-[9px] "></div>
            <UploadIcon />
            <input className="hidden" type="file" ref={inputRef} accept="jpg,jpeg,png" onChange={handleUpload} />
          </button>
        </div>
        {file && (
          <div className="flex items-center text-lg mt-3">
            <span className="text-[#0500F9] mx-3  font-medium">{shortenFileName(file)}</span>
            <a href="#close" onClick={() => setFile(undefined)}>
              <CloseIcon color="red" />
            </a>
          </div>
        )}
      </div>
      {renderErrorMessage()}
      <p className='xl:text-[16px] font-light  xl:ml-[1em] mt-[2em]'>Choose a color that best fit the brand</p>
      {selectedColor && (
        <div className='ml-[1em] '
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: selectedColor,
            marginTop: '20px',
           

           }}
        ></div>
      )}
        <div className='xl:ml-[1em] mt-[1em] hidden lg:block '> 
      <SwatchesPicker 
      onChange={handleColorChange}
         width={502}
    className="w-[502px]"
      />
  
      </div>
      <div className='xl:ml-[1em] mt-[1em] hidden lg:hidden md:block'> 
      <SwatchesPicker 
      onChange={handleColorChange}
         width={434}
    className="w-[434px]"
      />
  
      </div>
      <div className=' mt-[1em] flex md:hidden'> 
      <SwatchesPicker 
      onChange={handleColorChange}
       className="w-[350px]"
    
      />
  
      </div>
      <div className="flex xl:gap-[2.5em] xl:ml-[1em] 2xl:ml-[18.8em] xl:mt:-[6em] ml-[-0.1em] gap-[1em] md:gap-[2em] 414:ml-[0.5em] bg-[#F2F9FD] mt-[2em] 412:ml-[1.2em] 540:ml-[1em] biglg:ml-[1em] lg:ml-[1em] lg:mt-[5em] biglg:mt-[5em] md:ml-[0.6em] bigmd:ml-[2em] 3xl:ml-[1.5em] ">
        <button
          className="bg-[#ffffff] text-[16px]  h-[48px]  w-[150px] border-red-500 rounded-md text-[#044E97] ml:[2em] md:mr-[-1.5em] mt-[2.7em] md:mt-[2.9em] xl:mt-[2em] md:w-[200px] lg:mr-[-0.5em]  lg:mt-[-2em] xl:w-[200px] xl:mr-[-0.5em] mb-[2em]"
          onClick={handlePrevious}
        >
          Previous Step
        </button>
        <button
          className="bg-[#044E97] text-[16px]  h-[48px] 540:mr-[5em] w-[150px]  rounded-md text-[#ffffff] ml:[2em] md:mr-[-1.5em] mt-[2.7em] md:mt-[2.9em] xl:mt-[2em] md:w-[200px] lg:mr-[-0.5em]  lg:mt-[-2em] xl:w-[200px] xl:mr-[-0.5em] mb-[2em]"
          onClick={handleNext}
          disabled={isLoading}
        >
   {isLoading ? 'Loading...' : 'Next Step'}
        </button>
      </div>
     
    </div>
    </>
  )
}

export default BrandIdentity